import { captureMessage } from '@sentry/vue';
import { OUTFIT_CATEGORY_KEY } from '@/constants';
import { GenderEnum } from '@/enum/gender';
import DetailedOutfitHelper from '@/services/DetailedOutfitHelper';
import {
  MixAndMatchCategoryEnum,
  MixAndMatchUnderwearEnum,
  MixAndMatchDefaultEnum,
} from '@/enum/mix_and_match';
import { OutfitTypeEnum } from '@/enum/outfit_type';
import { snapchat } from '@/gen/proto/protos';
import IAssetsV3 = snapchat.bitmoji.api.IAssetsV3;
import IMixAndMatchItemsCategory = snapchat.bitmoji.api.IMixAndMatchItemsCategory;
import IMixAndMatchItemsBrand = snapchat.bitmoji.api.IMixAndMatchItemsBrand;
import IGarment = snapchat.bitmoji.api.IGarment;
import IColorGroupTones = snapchat.bitmoji.api.IColorGroupTones;
import { FashionColourPickerConfig } from '@/services/ColourPickerOptionsHelper';
import ITone = snapchat.bitmoji.api.ITone;

const TOP_UNDERWEAR_COLOUR_PALETTE: Record<string, number> = {
  top_tone1: 16448250,
  top_tone2: 16448250,
  top_tone3: 16448250,
  top_tone4: 16448250,
  top_tone5: 14145495,
  top_tone6: 15508871,
  top_tone7: 15066597,
  top_tone8: 15066597,
  top_tone9: 5057046,
  top_tone10: 10253397,
};

const BOTTOM_UNDERWEAR_COLOUR_PALETTE: Record<string, number> = {
  bottom_tone1: 16448250,
  bottom_tone2: 16448250,
  bottom_tone3: 16448250,
  bottom_tone4: 16448250,
  bottom_tone5: 14145495,
  bottom_tone6: 15508871,
  bottom_tone7: 15066597,
  bottom_tone8: 15066597,
  bottom_tone9: 5057046,
  bottom_tone10: 10253397,
};

const DEFAULT_UNDERWEAR_OUTFIT_ID: number = 895546;

const getMixAndMatchCategoriesDataFromAssets = (
  gender: GenderType,
  assets: IAssetsV3,
): MixAndMatchCategoriesData[] | null => {
  const mixAndMatchCategoriesData: MixAndMatchCategoriesData[] = [];
  const categories = assets?.mix_and_match_items?.[gender]?.categories;
  if (!categories) {
    captureMessage(`[MixAndMatchHelper] no mix and match assets available for gender: ${gender}`);
    return null;
  }

  DetailedOutfitHelper.DETAILED_OUTFIT_KEYS.forEach(function(key: string) {
    const category: IMixAndMatchItemsCategory | undefined = categories.find(function(category) {
      return category.name === key;
    });
    if (!category || !category.brands) {
      captureMessage(`[MixAndMatchHelper] ${category} has no brands`);
      return;
    }
    const brands: IMixAndMatchItemsBrand[] = category.brands;
    const removeOption: IGarment = {
      id: 0,
      color_group_ids: [],
    };

    const lastBrandGarments: IGarment[] | undefined | null =
      category?.brands[category?.brands.length - 1]?.garments;
    if (lastBrandGarments && lastBrandGarments[lastBrandGarments.length - 1].id !== 0) {
      // Add remove option to the last brand
      lastBrandGarments.push(removeOption);
    }

    if (category?.name) {
      mixAndMatchCategoriesData.push({
        key: category.name,
        options: brands,
      });
    }
  });
  return mixAndMatchCategoriesData;
};

const keysToDeleteForMixAndMatchAction = (
  category: string,
  currentOutfitType: number,
): string[] => {
  if (currentOutfitType === OutfitTypeEnum.ORIGINAL) {
    // If the user is wearing an original type outfit, delete all of the mix and match keys.
    return DetailedOutfitHelper.keysToDelete();
  } else if (currentOutfitType === OutfitTypeEnum.SPLIT) {
    // If the user is wearing a detailed outfit, delete only the colour tone keys for the category in which the item was selected.
    return DetailedOutfitHelper.CATEGORY_CUSTOM_COLOUR_KEYS[category];
  } else {
    // Otherwise, the user is wearing the full underwear. Delete only the outfit key because they have never saved an outfit before.
    return [OUTFIT_CATEGORY_KEY];
  }
};

const colourPickerConfigForSelectedItem = (
  selectedCategory: string,
  selectedItemId: number,
  categories: AllCategories[],
  colourGroups: Record<number, IColorGroupTones>,
): FashionColourPickerConfig | null => {
  // Return null if the user is not on a mix and match category or if there is no selected item for that category.
  if (
    !DetailedOutfitHelper.DETAILED_OUTFIT_KEYS.includes(selectedCategory) ||
    <unknown>selectedItemId === '0' ||
    selectedItemId === undefined ||
    selectedItemId === underwearIdForCategory(selectedCategory)
  ) {
    return null;
  }
  // Get the full selected category object
  const selectedCategoryObject: AllCategories | undefined = categories.find(function(category) {
    return category.key === selectedCategory;
  });
  if (!selectedCategoryObject || !selectedCategoryObject.options) {
    return null;
  }
  // Get the selected item object to get the colour group ids.
  let categoryOptions: IGarment[] = [];
  selectedCategoryObject.options.forEach((brand: any) => {
    if ('garments' in brand) {
      categoryOptions = categoryOptions.concat(brand.garments);
    }
  });

  const selectedItem = categoryOptions.find(function(item) {
    return item.id === selectedItemId;
  });
  if (!selectedItem) {
    return null;
  }
  const colourGroupIds = selectedItem?.color_group_ids;
  if (!colourGroupIds) {
    return null;
  }

  let colourPickerOptions: ITone[] = [];

  // Get the colour swatch sets that correspond to the colour group ids
  colourGroupIds.forEach(function(colourGroupId: number) {
    if (colourGroups[colourGroupId]?.tones) {
      const tones = colourGroups[colourGroupId]?.tones;

      // Tones *should* exist for every group id unless metadata is malformed
      if (tones) {
        colourPickerOptions = colourPickerOptions.concat(tones);
      } else {
        captureMessage(
          `Mix and match option (id: ${selectedItemId}) references colour group (id: ${colourGroupId}) with missing tone data`,
        );
      }
    }
  });
  const secondaryColours = selectedItem.secondary_colors ? selectedItem.secondary_colors : null;

  return new FashionColourPickerConfig(colourPickerOptions, secondaryColours || undefined);
};

const colourPaletteForSelectedItemInCategory = (
  category: string,
  traits: Record<string, number>,
): Record<string, number> => {
  const colourPaletteKeys = DetailedOutfitHelper.CATEGORY_CUSTOM_COLOUR_KEYS[category];
  const colourPalette: Record<string, number> = {};
  colourPaletteKeys.forEach(function(colourPaletteKey) {
    if (colourPaletteKey in traits) {
      colourPalette[colourPaletteKey] = traits[colourPaletteKey];
    }
  });
  return colourPalette;
};

/**
 * Parses colour options out of the currently selected traits
 */
const getSelectedColourPickerOptionFromTraits = (
  clothingCategory: string,
  selectedTraits: Record<string, number>,
): ITone | null => {
  const colorTraitPrefix = `${clothingCategory}_tone`;
  return Object.entries(selectedTraits)
    .filter(([traitKey]) => traitKey.startsWith(colorTraitPrefix))
    .reduce((prev, [traitKey, colourValue]) => {
      return {
        ...prev,
        [traitKey]: colourValue,
      };
    }, {});
};

const underwearIdForCategory = (category: string): number => {
  if (category === MixAndMatchCategoryEnum.TOP) {
    return MixAndMatchUnderwearEnum.UNDERWEAR_TOP;
  } else if (category === MixAndMatchCategoryEnum.BOTTOM) {
    return MixAndMatchUnderwearEnum.UNDERWEAR_BOTTOM;
  }
  return 0;
};

const isUnderwearAvailableForCategory = (category: string, gender: GenderEnum): boolean => {
  if (
    (category === MixAndMatchCategoryEnum.TOP && gender == GenderEnum.FEMALE) ||
    category === MixAndMatchCategoryEnum.BOTTOM
  ) {
    // Females have top underwear and both genders have bottom underwear
    return true;
  }
  return false;
};

const underwearColourPaletteForCategory = (category: string): Record<string, number> => {
  if (category === MixAndMatchCategoryEnum.TOP) {
    return TOP_UNDERWEAR_COLOUR_PALETTE;
  } else if (category === MixAndMatchCategoryEnum.BOTTOM) {
    return BOTTOM_UNDERWEAR_COLOUR_PALETTE;
  }
  return {};
};

// Return the bodysuit if an avatar is wearing a bodysuit top or a full (both top and bottom) bodysuit
// Return null if not wearing a bodysuit at all
const getCurrentBodysuit = (
  selectedTraitsTop: number,
  categories: AllCategories[],
): IGarment | null => {
  const topOptions = categories.find((category) => {
    return category.key === MixAndMatchCategoryEnum.TOP;
  });

  if (!topOptions || !topOptions.options) {
    return null;
  }
  for (const brand of topOptions.options) {
    const mixAndMatchItemsBrand = <IMixAndMatchItemsBrand>brand;
    if (mixAndMatchItemsBrand?.garments) {
      for (const top of mixAndMatchItemsBrand.garments) {
        if (top?.is_bodysuit && top.id === selectedTraitsTop) {
          return top;
        }
      }
    }
  }

  return null;
};

// Only return true if an avatar is wearing a full bodysuit (both top and bottom)
const isWearingFullBodysuit = (
  selectedTraitsBottom: number,
  selectedTraitsTop: number,
  categories: AllCategories[],
): boolean | null => {
  const currentTop = getCurrentBodysuit(selectedTraitsTop, categories);
  return currentTop && selectedTraitsBottom === currentTop.id;
};

// Return colour palette where all top keys are replaced with bottom keys
// This is used specifically for bodysuits
const replacePaletteTopWithBottomKey = (
  palette: Record<string, number>,
): Record<string, number> => {
  const paletteWithBottomKeys: Record<string, number> = {};
  Object.keys(palette).forEach((key) => {
    const newBottomKey = key.replace(MixAndMatchCategoryEnum.TOP, MixAndMatchCategoryEnum.BOTTOM);
    paletteWithBottomKeys[newBottomKey] = palette[key];
  });
  return paletteWithBottomKeys;
};

const getDefaultOutfitTraits = () => {
  let traits: Record<string, number> = {};

  traits.top = MixAndMatchDefaultEnum.TOP;
  traits.bottom = MixAndMatchDefaultEnum.BOTTOM;
  traits.footwear = MixAndMatchDefaultEnum.FOOTWEAR;
  traits.clothing_type = OutfitTypeEnum.SPLIT;

  Object.keys(BOTTOM_UNDERWEAR_COLOUR_PALETTE).forEach((colourKey) => {
    traits[colourKey] = BOTTOM_UNDERWEAR_COLOUR_PALETTE[colourKey];
  });
  Object.keys(TOP_UNDERWEAR_COLOUR_PALETTE).forEach((colourKey) => {
    traits[colourKey] = TOP_UNDERWEAR_COLOUR_PALETTE[colourKey];
  });

  return traits;
};

const isDefault = (traits: Record<string, number>): boolean => {
  const containsEachTone = (palette: Record<string, number>): boolean => {
    for (const tone in palette) {
      if (traits[tone] !== palette[tone]) {
        return false;
      }
    }
    return true;
  };
  return (
    (traits[MixAndMatchCategoryEnum.TOP] === MixAndMatchDefaultEnum.TOP &&
      traits[MixAndMatchCategoryEnum.BOTTOM] === MixAndMatchDefaultEnum.BOTTOM &&
      traits[MixAndMatchCategoryEnum.FOOTWEAR] === MixAndMatchDefaultEnum.FOOTWEAR &&
      containsEachTone(TOP_UNDERWEAR_COLOUR_PALETTE) &&
      containsEachTone(BOTTOM_UNDERWEAR_COLOUR_PALETTE) &&
      traits[MixAndMatchCategoryEnum.OUTERWEAR] === undefined &&
      traits[MixAndMatchCategoryEnum.SOCK] === undefined &&
      traits[MixAndMatchCategoryEnum.BODYSUIT] === undefined) ||
    traits[OUTFIT_CATEGORY_KEY] === DEFAULT_UNDERWEAR_OUTFIT_ID
  );
};

// Special case handle for one_piece category.
// Handle one_piece as top since one_piece acts as a top and bottom combined
// (similar to bodysuit in top category).
const getItemCategory = (category: string): string => {
  return category === MixAndMatchCategoryEnum.DRESS ? MixAndMatchCategoryEnum.TOP : category;
};

export default {
  BOTTOM_UNDERWEAR_COLOUR_PALETTE,
  TOP_UNDERWEAR_COLOUR_PALETTE,
  keysToDeleteForMixAndMatchAction,
  colourPickerConfigForSelectedItem,
  colourPaletteForSelectedItemInCategory,
  getSelectedColourPickerOptionFromTraits,
  isDefault,
  isUnderwearAvailableForCategory,
  underwearIdForCategory,
  underwearColourPaletteForCategory,
  getCurrentBodysuit,
  isWearingFullBodysuit,
  replacePaletteTopWithBottomKey,
  getMixAndMatchCategoriesDataFromAssets,
  getDefaultOutfitTraits,
  getItemCategory,
};
